import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import LibAccordion from 'react-bootstrap/Accordion';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';

import { theme } from '_constants/theme';

const { colors, spaces, devices } = theme;

const StyledContainer = styled.div`
  ${({ faqList }) =>
    faqList &&
    css`
      border-bottom: 1px solid ${colors.gray[200]};
      padding: 20px;
    `}

  @media ${devices.tablet} {
    ${({ faqList }) =>
      faqList &&
      css`
        padding-left: 0px;
        padding-right: 0px;
        padding-top: 16px;
        padding-bottom: 16px;
      `}
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledHeader = styled(forwardRef(({ highlighted, noTitlePadding, topSpace, bottomSpace, ...props }, ref) => <div ref={ref} {...props} />))`
  display: flex;
  padding-top: ${spaces.small2X}px;
  padding-bottom: ${spaces.small2X}px;
  cursor: pointer;

  ${({ noTitlePadding }) =>
    noTitlePadding &&
    css`
      padding: 0;
    `}

  ${({ highlighted }) =>
    highlighted &&
    css`
      background: ${colors.gray[200]};
    `}

  ${({ topSpace }) =>
    topSpace &&
    css`
      margin-top: ${typeof topSpace === 'number' ? topSpace : spaces.regular}px;
    `}

  ${({ bottomSpace }) =>
    bottomSpace &&
    css`
      margin-bottom: ${typeof bottomSpace === 'number'
        ? bottomSpace
        : spaces.regular}px;
    `}
`;

const StyledIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: ${spaces.regular}px;
`;

const StyledTitle = styled.div`
  flex-grow: 1;
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledBody = styled(forwardRef(({ noBodyMargin, ...props }, ref) => <div ref={ref} {...props} />))`
  margin-top: ${spaces.regular}px;

  ${({ noBodyMargin }) =>
    noBodyMargin &&
    css`
      margin: 0;
    `}
`;

const AccordionItem = ({
  eventKey,
  title,
  children,
  open,
  openIcon,
  closeIcon,
  topSpace,
  bottomSpace,
  noTitlePadding,
  noBodyMargin,
  highlighted,
  onToggle,
  faqList,
}) => {
  const handleToggle = useAccordionButton(
    eventKey,
    () => onToggle && onToggle(eventKey)
  );

  return (
    <StyledContainer faqList={faqList}>
      <StyledHeader
        highlighted={highlighted}
        noTitlePadding={noTitlePadding}
        topSpace={topSpace}
        bottomSpace={bottomSpace}
        onClick={handleToggle}
      >
        <StyledTitle>{title}</StyledTitle>
        <StyledIconContainer>{open ? openIcon : closeIcon}</StyledIconContainer>
      </StyledHeader>
      <LibAccordion.Collapse eventKey={eventKey}>
        <StyledBody noBodyMargin={noBodyMargin}>{children}</StyledBody>
      </LibAccordion.Collapse>
    </StyledContainer>
  );
};

AccordionItem.propTypes = {
  eventKey: PropTypes.string,
  title: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  open: PropTypes.bool,
  openIcon: PropTypes.node,
  closeIcon: PropTypes.node,
  noTitlePadding: PropTypes.bool,
  noBodyMargin: PropTypes.bool,
  highlighted: PropTypes.bool,
  topSpace: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  bottomSpace: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  onToggle: PropTypes.func,
  faqList: PropTypes.bool,
};

export default AccordionItem;
